import React from "react";
import {
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
} from "recharts";
import { SampleSet } from "@/api/qpcr";

const COLORS = [
  "#2563EB", "#DC2626", "#7C3AED", "#059669",
  "#CA8A04", "#0EA5E9", "#DB2777", "#65A30D",
];

interface PlotPoint {
  cycle: number;
  [seriesName: string]: number | null;
}

export function SampleSetPlot({ sampleSet }: { sampleSet: SampleSet }) {
  const cycles = Math.max(
    sampleSet.control_positive.length,
    sampleSet.control_ntc.length,
    ...sampleSet.wells.map(w => w.length),
  );

  const data: PlotPoint[] = [];
  for (let c = 0; c < cycles; c++) {
    const point: PlotPoint = { cycle: c };
    point["control+"] = sampleSet.control_positive[c] ?? null;
    point["control-"] = sampleSet.control_ntc[c] ?? null;
    sampleSet.wells.forEach((well, i) => {
      point[`well ${i + 1}`] = well[c] ?? null;
    });
    data.push(point);
  }

  const seriesNames = [
    "control+",
    "control-",
    ...sampleSet.wells.map((_, i) => `well ${i + 1}`),
  ];

  const title = sampleSet.pathogen_name ?? `pathogen ${sampleSet.pathogen_id ?? "?"}`;

  return (
    <section className="rounded-md border border-border bg-surface p-md space-y-sm">
      <h3 className="text-sm font-medium">{title}</h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 12, bottom: 8, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis dataKey="cycle" stroke="#6B7280" fontSize={11} />
            <YAxis stroke="#6B7280" fontSize={11} />
            <Tooltip contentStyle={{ fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {seriesNames.map((name, idx) => (
              <Line
                key={name}
                type="monotone"
                dataKey={name}
                stroke={COLORS[idx % COLORS.length]}
                strokeDasharray={name.startsWith("control") ? "4 2" : undefined}
                dot={false}
                isAnimationActive={false}
                connectNulls={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}
